import React, { useState, useEffect, useRef } from 'react';
import { Box, Button, Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions, CircularProgress } from '@mui/material';
import { Delete, Translate, Save } from '@mui/icons-material';
import { Modal, Select } from 'antd';
import SunEditor from 'suneditor-react';
import styled from 'styled-components';
import { SPANISH_ITEM_LOWER, SPANISH_ITEM_UPPER } from 'utils/config/reference';
import {
  blockquote,
  align,
  font,
  fontSize,
  fontColor,
  hiliteColor,
  horizontalRule,
  list,
  table,
  formatBlock,
  lineHeight,
  template,
  paragraphStyle,
  textStyle,
  link,
  image,
} from "suneditor/src/plugins";
import { useCurrentUser } from 'store/users/selectors';
import { useDispatch } from 'react-redux';
import { handleGenerateEnglishLessonPlan } from 'store/lesson_plan/thunks';
import { useAlert } from 'components/UI/alertSakeBar/alertProvider';
import SentryWrap from 'components/Sentry';
import HTMLContentPrinter from './HTMLContentPrinter';



// ======================= Custom Plugins =======================
// Lowercase Spanish
const SPANISH_ITEM_LOWER_PLUGIN = {
  name: "SPANISH_ITEM_LOWER",
  display: "submenu",
  innerHTML: `<span class="spnshHeadTxt">á</span>`,
  add: function (core, targetElement) {
    let listDiv = this.setSubmenu.call(core);
    var self = this;
    listDiv.querySelectorAll(".se-btn-list-lower").forEach(function (btn) {
      btn.addEventListener("click", self.onClick.bind(core));
    });
    core.initMenuTarget(this.name, targetElement, listDiv);
  },
  setSubmenu: function () {
    const listDiv = this.util.createElement("DIV");
    listDiv.className = "se-submenu se-list-layer";
    listDiv.innerHTML = `<div class="se-list-inner se-list-font-size" style="width:30px;">
      <ul class="se-list-basic">
         ${SPANISH_ITEM_LOWER.map(
           (item) =>
             `<li><button type="button" class="se-btn-list-lower" value="${item}">${item}</button></li>`
         ).join("")}
      </ul></div>`;
    return listDiv;
  },
  onClick: function (e) {
    e.preventDefault();
    const value = e.target.value;
    const node = this.util.createElement("span");
    node.textContent = value;
    this.insertNode(node);

    const zeroWidthSpace = this.util.createTextNode(this.util.zeroWidthSpace);
    node.parentNode.insertBefore(zeroWidthSpace, node.nextSibling);
    this.submenuOff();

    // ✅ Trigger change
    if (this.functions.onChange) {
      this.functions.onChange(this.getContents(), this);
    }
  },
};

// Uppercase Spanish
const SPANISH_ITEM_UPPER_PLUGIN = {
  name: "SPANISH_ITEM_UPPER",
  display: "submenu",
  innerHTML: `<span class="spnshHeadTxt">Á</span>`,
  add: function (core, targetElement) {
    let listDiv = this.setSubmenu.call(core);
    var self = this;
    listDiv.querySelectorAll(".se-btn-list-upper").forEach(function (btn) {
      btn.addEventListener("click", self.onClick.bind(core));
    });
    core.initMenuTarget(this.name, targetElement, listDiv);
  },
  setSubmenu: function () {
    const listDiv = this.util.createElement("DIV");
    listDiv.className = "se-submenu se-list-layer";
    listDiv.innerHTML = `<div class="se-list-inner se-list-font-size" style="width:30px;">
      <ul class="se-list-basic">
         ${SPANISH_ITEM_UPPER.map(
           (item) =>
             `<li><button type="button" class="se-btn-list-upper" value="${item}">${item}</button></li>`
         ).join("")}
      </ul></div>`;
    return listDiv;
  },
  onClick: function (e) {
    e.preventDefault();
    const value = e.target.value;
    const node = this.util.createElement("span");
    node.textContent = value;
    this.insertNode(node);

    const zeroWidthSpace = this.util.createTextNode(this.util.zeroWidthSpace);
    node.parentNode.insertBefore(zeroWidthSpace, node.nextSibling);
    this.submenuOff();

    if (this.functions.onChange) {
      this.functions.onChange(this.getContents(), this);
    }
  },
};


// ======================= Main Component =======================
/**
 * LessonPlanEditor Component
 * 
 * Features:
 * - Edit lesson plan content in Spanish or English
 * - Always defaults to Spanish content (users must manually switch to English)
 * - Language switching dropdown (when English content exists)
 * - Generate English content button (when English content doesn't exist)
 * - Real-time language indicator
 * - Loading states for all operations
 * 
 * Behavior:
 * - Default language: Spanish
 * - After generating English content: Automatically switches to English view
 * - On new sessions: Always starts with Spanish content
 * 
 * Props:
 * - existingLessonPlan: Object containing lesson plan data with optional content_english field
 * - onUpdate: Function called when updating lesson plan (receives updateData object)
 * - onDelete: Function called when deleting lesson plan
 * - isUpdating: Boolean indicating if update is in progress
 * - title: String for modal title
 */
const LessonPlanEditor = ({
  selectedPagePlan,
  open,
  onClose,
  existingLessonPlan,
  onUpdate,
  onDelete,
  isUpdating = false,
  title = "Edit Lesson Plan",
  onPrintSingle,
  printLoader,
}) => {
  const currentUser = useCurrentUser();
  const dispatch = useDispatch();
  const showAlert = useAlert();
  const [content, setContent] = useState('');
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [combinedContent, setCombinedContent] = useState(''); 
  const [currentLanguage, setCurrentLanguage] = useState('spanish');
  const [isGeneratingEnglish, setIsGeneratingEnglish] = useState(false);
  const editorRef = useRef(null);
  const originalContentRef = useRef(null);
  const [hasModifiedChange, setHasModifiedChange] = useState(false);
  // Load existing lesson plan content
  // useEffect(() => {
  //   if (existingLessonPlan?.content) {
  //     setContent(existingLessonPlan.content);
  //   } else {
  //     setContent('');
  //   }
  // }, [existingLessonPlan, open]);

  useEffect(() => {
    if (existingLessonPlan?.content) {
      setCombinedContent(existingLessonPlan.content);
      // Always default to Spanish content
      setCurrentLanguage('spanish');
      setContent(existingLessonPlan.content);
      originalContentRef.current = existingLessonPlan.content;
    }
  }, [existingLessonPlan]);

  // Update content when language changes
  useEffect(() => {
    if (existingLessonPlan) {
      if (currentLanguage === 'spanish' && existingLessonPlan.content) {
        setContent(existingLessonPlan.content);
        originalContentRef.current = existingLessonPlan.content;
      } else if (currentLanguage === 'english' && existingLessonPlan.content_english) {
        setContent(existingLessonPlan.content_english);
        originalContentRef.current = existingLessonPlan.content_english;
      }
    }
  }, [currentLanguage, existingLessonPlan]);

  useEffect( () => {
    const originalContent = originalContentRef.current.trim().split(" ").join("").split("\n").join("");
    const currentContent = content.trim().split(" ").join("");
    const hasContentChanged = originalContent !== currentContent;
    setHasModifiedChange(hasContentChanged);
  }, [content, originalContentRef.current, currentLanguage]);

  const handleContentChange = (val) => {
    setContent(val);
  };

  const handleLanguageChange = (value) => {
    setCurrentLanguage(value);
  };

  const handleGenerateEnglishContent = async () => {
    if (!existingLessonPlan?.content) {
      showAlert('No Spanish content available to translate', 'error');
      return;
    }

    setIsGeneratingEnglish(true);
    try {

        const result = await dispatch(handleGenerateEnglishLessonPlan(existingLessonPlan.id, currentUser.id, existingLessonPlan.chapter,existingLessonPlan.created_by=="admin"));
      if (result && result.content_english) {  
        // Update the content in the editor and switch to English view
        setContent(result.content_english);
        setCurrentLanguage('english');
        originalContentRef.current = result.content_english;
        
        showAlert('English content generated successfully! Switched to English view.', 'success');
      } else {
        showAlert('Failed to generate English content', 'error');
      }
    } catch (error) {
      console.error('Error generating English content:', error);
      showAlert('Error generating English content', 'error');
    } finally {
      setIsGeneratingEnglish(false);
    }
  };

  const handleUpdate = () => {
    if (onUpdate) {
      // Update the appropriate field based on current language
      const updateData = {
        content: currentLanguage === 'spanish' ? content : existingLessonPlan.content,
        content_english: currentLanguage === 'english' ? content : existingLessonPlan.content_english,
      };
      onUpdate(updateData);
    }
  };
  

  const handleDeleteClick = () => {
    setShowDeleteDialog(true);
  };

  const handleDeleteConfirm = () => {
    if (onDelete) {
      onDelete();
    }
    setShowDeleteDialog(false);
  };

  const handleDeleteCancel = () => {
    setShowDeleteDialog(false);
  };
  return (
  <SentryWrap>
    <ModalStyles>
      <Modal
        open={open}
        onCancel={onClose}
        footer={null}
        width="80%"
        zIndex={2000}
        centered
        style={{ 
          maxWidth: "1000px",
          paddingBottom: 0,
          top: 0,
          margin: 0
        }}
        styles={{
          body: {
            height: "80vh",
            padding: 0,
            margin: 0
          }
        }}
        wrapClassName="lesson-plan-editor-modal"
      >
      <EditorModalContent>
        <Row style={{ justifyContent: "space-between", marginBottom: 30 }}>
          <Box style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <TitleText>{title}</TitleText>
            
          </Box>
          <Box className="editLessonPlanUpdateSpanishButton">
            {/* {existingLessonPlan?.content_english && (
              <Box style={{ 
                padding: '4px 8px', 
                backgroundColor: currentLanguage === 'spanish' ? '#e3f2fd' : '#f3e5f5',
                borderRadius: '4px',
                fontSize: '12px',
                fontWeight: 'bold',
                color: currentLanguage === 'spanish' ? '#1976d2' : '#7b1fa2'
              }}>
                {currentLanguage === 'spanish' ? '🇪🇸 Spanish' : '🇺🇸 English'}
              </Box>
            )} */}
            {existingLessonPlan?.content_english && (
              <StyledSelect
                value={currentLanguage}
                onChange={handleLanguageChange}
                style={{ minWidth: 120, height: 36 }}
                size="small"
                placement="bottomLeft"
                getPopupContainer={() => document.body}
                dropdownStyle={{ zIndex: 3000 }}
                options={[
                  { value: 'spanish', label: '🇪🇸 Spanish' },
                  { value: 'english', label: '🇺🇸 English' }
                ]}
              />
            )}
            
            {existingLessonPlan?.content && !existingLessonPlan?.content_english && (
              <Button
                variant="contained"
                color="primary"
                onClick={handleGenerateEnglishContent}
                disabled={isGeneratingEnglish || isUpdating}
                size="small"
                startIcon={isGeneratingEnglish ? <CircularProgress size={16} /> : <Translate />}
                sx={{
                  background: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
                  height: '36px',
                  minHeight: '36px',
                  '&:hover': {
                    background: 'linear-gradient(135deg, #3a8ee6 0%, #00d4e6 100%)',
                  },
                  '&:disabled': {
                    background: 'rgba(0, 0, 0, 0.12)',
                  }
                }}
              >
                {isGeneratingEnglish ? 'Generating...' : 'Generate English'}
              </Button>
            )}
            
            <Button
              variant="contained"
              color="success"
              onClick={handleUpdate}
              disabled={!content.trim() || isUpdating}
              size="small"
              startIcon={<Save />}
              sx={{
                background: 'linear-gradient(135deg, #11998e 0%, #38ef7d 100%)',
                height: '36px',
                minHeight: '36px',
                '&:hover': {
                  background: 'linear-gradient(135deg, #0f8078 0%, #2ed368 100%)',
                },
                '&:disabled': {
                  background: 'rgba(0, 0, 0, 0.12)',
                }
              }}
            >
              {isUpdating ? 'Updating...' : 'Update'}
            </Button>

            {selectedPagePlan?.content && selectedPagePlan?.content_english && (
            <HTMLContentPrinter
              onPrintSingle={onPrintSingle}
              printLoader={printLoader}
              updateContent={handleUpdate}
              // htmlContent={
              //   currentLanguage === 'english' && selectedPagePlan?.content_english 
              //         ? selectedPagePlan.content_english 
              //         : selectedPagePlan?.content
              // }
              htmlContent={content}
              hasModifiedChange={hasModifiedChange}
            />
            )}

            {existingLessonPlan && onDelete && existingLessonPlan.created_by === currentUser?.id && (
              <Button
                variant="contained"
                color="error"
                onClick={handleDeleteClick}
                disabled={isUpdating}
                size="small"
                startIcon={<Delete sx={{ color: 'white !important' }} />}
                sx={{
                  background: 'linear-gradient(135deg, #f857a6 0%, #ff5858 100%)',
                  color: 'white',
                  height: '36px',
                  minHeight: '36px',
                  '&:hover': {
                    background: 'linear-gradient(135deg, #e04395 0%, #e84343 100%)',
                  },
                  '&:disabled': {
                    background: 'rgba(0, 0, 0, 0.12)',
                  }
                }}
              >
                {isUpdating ? 'Deleting...' : 'Delete'}
              </Button>
            )}
          </Box>
        </Row>
        
        <Box className="editLessonPlanSuneditor">
          <Box style={{ flex: 1, minHeight: 0, paddingBottom: '30px' }}>
            <Box style={{ borderBottom:"0.5px solid #80808094" }}>

            <SunEditor
                ref={editorRef}
                defaultValue={content || ''}
                onChange={handleContentChange}
                setContents={content || ''}
                setOptions={{
                  height: 'calc(60vh - 100px)',
                  plugins: [
                    blockquote,
                    align,
                    font,
                    fontSize,
                    fontColor,
                    hiliteColor,
                    horizontalRule,
                    list,
                    table,
                    formatBlock,
                    lineHeight,
                    template,
                    paragraphStyle,
                    textStyle,
                    link,
                    image,
                    SPANISH_ITEM_LOWER_PLUGIN,
                    SPANISH_ITEM_UPPER_PLUGIN,
                  ],
                  buttonList: [
                    ['undo', 'redo'],
                    ['font', 'fontSize', 'formatBlock'],
                    ['paragraphStyle', 'blockquote'],
                    ['bold', 'underline', 'italic'],
                    ['fontColor', 'hiliteColor', 'textStyle'],
                    ['removeFormat'],
                    ['outdent', 'indent'],
                    ['align', 'horizontalRule', 'list', 'lineHeight'],
                    ['table'],
                    ['SPANISH_ITEM_LOWER', 'SPANISH_ITEM_UPPER'], // ✅ Add plugin buttons here
                  ],
                  placeholder: 'Edit your lesson plan content...'
                }}
              />



            </Box>
          </Box>
        </Box>
      </EditorModalContent>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={showDeleteDialog}
        onClose={handleDeleteCancel}
        aria-labelledby="delete-dialog-title"
        aria-describedby="delete-dialog-description"
        sx={{ zIndex: 3000 }}
      >
        <DialogTitle id="delete-dialog-title">
          Confirm Delete
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="delete-dialog-description">
            Are you sure you want to delete this lesson plan? This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDeleteCancel} color="primary">
            Cancel
          </Button>
          <Button 
            onClick={handleDeleteConfirm} 
            color="error" 
            variant="contained"
            disabled={isUpdating}
          >
            {isUpdating ? 'Deleting...' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
      </Modal>
    </ModalStyles>
    </SentryWrap>
  );
};

export default LessonPlanEditor;

// ======================= Styled Components =======================
const EditorModalContent = styled.div`
  background-color: white;
  height: 80vh;
  width: 100%;
  display: flex;
  flex-direction: column;
  padding: 30px;
  box-sizing: border-box;
  border-radius: 8px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
  overflow: auto;
`;

const Row = styled.div`
  display: flex;
  align-items: center;
`;

const StyledSelect = styled(Select)`
  .ant-select-dropdown {
    z-index: 3000 !important;
  }
  
  .ant-select-selector {
    background-color: #1976d2 !important;
    color: white !important;
    border-color: #1976d2 !important;
    border-radius: 4px !important;
    height: 36px !important;
    min-height: 36px !important;
    
    .ant-select-selection-item {
      color: white !important;
      font-weight: 500 !important;
      line-height: 34px !important;
    }

    .ant-select-arrow {
      color: white !important;
    }
  }

  &:hover .ant-select-selector {
    background-color: #1565c0 !important;
    border-color: #1565c0 !important;
  }

  &.ant-select-focused .ant-select-selector {
    box-shadow: 0 0 0 2px rgba(25, 118, 210, 0.2) !important;
    border-color: #1976d2 !important;
  }
`;

const TitleText = styled.h2`
  color: #333;
  font-size: 24px;
  font-weight: bold;
  margin: 0;
`;

const ModalStyles = styled.div`
  .lesson-plan-editor-modal {
    .ant-modal {
      top: 50% !important;
      transform: translateY(-50%) !important;
      margin: 0 auto !important;
    }
    
    .ant-modal-content {
      border-radius: 8px !important;
      overflow: hidden !important;
    }
    
    .ant-modal-body {
      padding: 0 !important;
      margin: 0 !important;
    }
  }
`;
